import {Box, Button, Card, type SxProps, type Theme, Tooltip, Typography, Unstable_Grid2 as Grid, useColorScheme} from "@mui/material";
import {DarkModeOutlined, LightModeRounded, SettingsBrightnessOutlined} from "@mui/icons-material";
import React from "react";
import {useTranslation} from "react-i18next";
import {useUpdateEffect} from "react-use";
import {LayoutSettingsCachedData} from "../../../../hooks/use-custom-saved-components-state/use-panel-layout-configuration";

export interface ColorModeButtonProps {
    colorMode: LayoutSettingsCachedData['themeMode'];
    setColorMode: React.Dispatch<React.SetStateAction<LayoutSettingsCachedData['themeMode']>>;
}

export const ColorModeButton: React.FC<ColorModeButtonProps> = ({setColorMode, colorMode}) => {
    const {setMode, mode} = useColorScheme();
    const {t} = useTranslation("components", {keyPrefix: 'panel-sections.drawers.settings-drawer.color-mode'});

    /**
     * Effect to change the color mode if the colorMode prop is different from the current mode
     */
    useUpdateEffect(() => {
        if (colorMode) {
            setMode(colorMode);
        }
    }, [colorMode, setMode])

    /**
     * Sets the theme mode to dark
     */
    const applyDarkMode = React.useCallback(() => {
        setMode('dark');
        setColorMode("dark")
    }, [setColorMode, setMode])

    /**
     * Sets the theme mode to light
     */
    const applyLightMode = React.useCallback(() => {
        setMode('light');
        setColorMode("light")
    }, [setMode, setColorMode])

    /**
     * Sets the theme mode system.
     */
    const applySystemMode = React.useCallback(() => {
        setMode('system');
        setColorMode("system")
    }, [setMode, setColorMode])

    const innerCardSx = React.useCallback((selected: boolean): SxProps<Theme> => {
        return ({
            minHeight: 100,
            borderRadius: 2,
            padding: 2,
            borderColor: (theme) => `rgba(${theme.vars.palette.grey['500Channel']} / 12%)`,
            backgroundColor: 'transparent',
            ...(selected && {
                backgroundColor: (theme) => `rgba(${theme.vars.palette.grey['500Channel']} / 8%)`,
            }),
            "&:hover": {
                backgroundColor: (theme) => `rgba(${theme.vars.palette.grey['500Channel']} / 8%)`,
            }
        });
    }, []);

    const togglerCardSx = React.useCallback((selectedMode: string): SxProps<Theme> => {
        const selected = mode === selectedMode;
        return ({
            minHeight: 50,
            borderRadius: 2,
            padding: 2,
            backgroundColor: 'transparent',
            transition: (theme) => theme.transitions.create(['border-color', 'background-color'], {duration: theme.transitions.duration.short}),
            borderColor: (theme) => `rgba(${theme.vars.palette.grey['500Channel']} / 0.12) !important`,
            ...(selected && {
                backgroundColor: (theme) => `rgba(${theme.vars.palette.grey['500Channel']} / 0.08)  !important`,
                borderColor: 'divider',
            }),
            "&:hover": {
                backgroundColor: (theme) => `rgba(${theme.vars.palette.grey['500Channel']} / 0.08)  !important`,
            }
        })
    }, [mode])

    return (
        <>
            <Card variant={'outlined'} sx={innerCardSx(false)}>
                <Box sx={{display: 'flex', flexDirection: 'column', gap: 2,}}>
                    <Grid container gap={1}>
                        <Tooltip title={t(t => t.dark)}>
                            <Grid component={Button} xs={true} color={'inherit'} variant={'text'} sx={togglerCardSx('dark')} onClick={applyDarkMode}>
                                <DarkModeOutlined/>
                            </Grid>
                        </Tooltip>
                        <Tooltip title={t(t => t.light)}>
                            <Grid component={Button} xs={true} color={'inherit'} variant={'text'} sx={togglerCardSx('light')} onClick={applyLightMode}>
                                <LightModeRounded/>
                            </Grid>
                        </Tooltip>
                        <Tooltip title={t(t => t.system)}>
                            <Grid component={Button} xs={true} color={'inherit'} variant={'text'} sx={togglerCardSx('system')} onClick={applySystemMode}>
                                <SettingsBrightnessOutlined/>
                            </Grid>
                        </Tooltip>
                    </Grid>
                    <Typography variant={'body2'} sx={{fontWeight: 600}}>
                        {t(t => t.title)}
                    </Typography>
                </Box>
            </Card>
        </>
    )
}